import { Link } from 'react-router-dom'
import LegalLayout from '../components/LegalLayout'
import SeoHead from '../components/SeoHead'
import { LEGAL, SITE } from '../lib/links'
import { orgSchema, breadcrumbSchema } from '../lib/seo'
import { useT, useLocale } from '../i18n/LocaleContext'
import { localizedPath } from '../i18n/routes'

const UPDATED_AT = '3 août 2026'

export default function PrivacyPage() {
  const { t } = useT()
  const locale = useLocale()
  if (locale === 'en') return <PrivacyPageEn />
  if (locale === 'nl') return <PrivacyPageNl />
  return (
    <>
      <SeoHead
        title="Politique de confidentialité — Yaslan"
        description="Politique de confidentialité de Yaslan Logistics BV. Comment nous collectons, utilisons et protégeons vos données personnelles dans le respect du RGPD."
        path="/privacy"
        jsonLd={[
          orgSchema(locale),
          breadcrumbSchema([
            { name: t('breadcrumb.home'), path: localizedPath('home', locale) },
            { name: t('breadcrumb.privacy'), path: localizedPath('privacy', locale) },
          ]),
        ]}
      />
      <LegalLayout
        breadcrumb="Politique de confidentialité"
        badge="Légal · RGPD"
        title={
          <>
            Politique de{' '}
            <span className="font-semibold bg-gradient-to-r from-accent-600 via-accent-500 to-accent-700 bg-clip-text text-transparent pb-[0.1em] inline-block">
              confidentialité
            </span>
            .
          </>
        }
        intro={
          <>
            Nous traitons vos données personnelles avec sérieux et dans le respect du Règlement Général
            sur la Protection des Données (RGPD — Règlement UE 2016/679) et de la loi belge du 30 juillet 2018.
          </>
        }
        updatedAt={UPDATED_AT}
      >
        <h2>1. Responsable du traitement</h2>
        <p>
          Le responsable du traitement de vos données personnelles est&nbsp;:
        </p>
        <ul>
          <li><strong>{LEGAL.company}</strong> ({LEGAL.legalForm}), exploitant la marque «&nbsp;{LEGAL.brand}&nbsp;»</li>
          <li>Numéro d'entreprise (BCE)&nbsp;: {LEGAL.bce}</li>
          <li>Numéro de TVA&nbsp;: {LEGAL.vat}</li>
          <li>Siège social&nbsp;: {LEGAL.registeredOffice.city}, Belgique</li>
          <li>E-mail&nbsp;: <a href={SITE.emailHref}>{SITE.email}</a></li>
          <li>Contact RGPD&nbsp;: <a href={`mailto:${LEGAL.rgpdContact}`}>{LEGAL.rgpdContact}</a></li>
        </ul>

        <h2>2. Données que nous collectons</h2>
        <p>
          Nous collectons uniquement les données strictement nécessaires à la fourniture de nos services
          logistiques et à la gestion de la relation commerciale. Selon le contexte&nbsp;:
        </p>
        <h3>2.1. Données fournies par nos clients (marques e-commerce)</h3>
        <ul>
          <li>Données d'identification professionnelle&nbsp;: nom de la société, numéro d'entreprise, TVA, adresse de facturation, nom et fonction du contact</li>
          <li>Données de contact&nbsp;: e-mail professionnel, numéro de téléphone</li>
          <li>Données contractuelles et financières&nbsp;: contrats, factures, modalités de paiement</li>
          <li>Données de connexion à notre dashboard logistique</li>
        </ul>
        <h3>2.2. Données des destinataires de colis (clients finaux de nos clients)</h3>
        <ul>
          <li>Identité&nbsp;: nom, prénom</li>
          <li>Coordonnées&nbsp;: adresse de livraison, e-mail, téléphone (lorsque transmis par le transporteur)</li>
          <li>Données de commande&nbsp;: référence, contenu, poids, transporteur, statut de livraison</li>
        </ul>
        <p>
          Pour ces données, <strong>{LEGAL.company} agit en qualité de sous-traitant</strong> au sens du RGPD&nbsp;:
          le responsable du traitement reste la marque e-commerce qui nous mandate.
        </p>
        <h3>2.3. Données collectées via le site web yaslan.be</h3>
        <ul>
          <li>Données techniques&nbsp;: adresse IP, type de navigateur, système d'exploitation, pages visitées, durée de visite</li>
          <li>Données de formulaire&nbsp;: informations volontairement renseignées via les formulaires de contact ou de prise de rendez-vous</li>
          <li>Cookies (voir notre <Link to="/cookies">Politique cookies</Link>)</li>
        </ul>

        <h2>3. Finalités et bases légales du traitement</h2>
        <p>Vos données sont traitées pour les finalités suivantes&nbsp;:</p>
        <ul>
          <li><strong>Exécution du contrat</strong> (art. 6.1.b RGPD)&nbsp;: réception, stockage, préparation et expédition des commandes, gestion des retours, facturation, support client</li>
          <li><strong>Obligation légale</strong> (art. 6.1.c RGPD)&nbsp;: conservation des factures (7 ans), déclarations TVA, obligations comptables et douanières</li>
          <li><strong>Intérêt légitime</strong> (art. 6.1.f RGPD)&nbsp;: amélioration de nos services, sécurité informatique, prévention de la fraude, prospection commerciale B2B</li>
          <li><strong>Consentement</strong> (art. 6.1.a RGPD)&nbsp;: cookies non essentiels, newsletter, communications marketing</li>
        </ul>

        <h2>4. Destinataires des données</h2>
        <p>
          Vos données ne sont jamais vendues. Elles peuvent être transmises uniquement aux
          catégories de destinataires suivantes, dans la stricte mesure nécessaire&nbsp;:
        </p>
        <ul>
          <li><strong>Transporteurs</strong> (bpost, DHL, GLS, UPS, DPD, FedEx, PostNL, etc.) — pour l'acheminement des colis</li>
          <li><strong>Plateformes e-commerce</strong> intégrées (Shopify, WooCommerce, Prestashop, Amazon, etc.) — uniquement à la demande du client</li>
          <li><strong>Sous-traitants techniques</strong>&nbsp;: hébergeur, fournisseur d'e-mail, outil de comptabilité, plateforme de paiement</li>
          <li><strong>Autorités publiques</strong>&nbsp;: en cas d'obligation légale ou de réquisition judiciaire</li>
        </ul>
        <p>
          Tous nos sous-traitants sont liés par un accord de traitement de données (DPA) conforme à
          l'article 28 RGPD.
        </p>

        <h2>5. Transferts hors Union européenne</h2>
        <p>
          Nous privilégions des prestataires établis dans l'Union européenne. Lorsqu'un transfert
          hors UE est nécessaire (par ex. transporteur express international), il est encadré par
          les Clauses Contractuelles Types adoptées par la Commission européenne ou par une décision
          d'adéquation.
        </p>

        <h2>6. Durée de conservation</h2>
        <ul>
          <li><strong>Données contractuelles et factures</strong>&nbsp;: 7 ans à compter de la fin du contrat (obligation comptable belge)</li>
          <li><strong>Données de prospection B2B</strong>&nbsp;: 3 ans à compter du dernier contact</li>
          <li><strong>Données de livraison</strong>&nbsp;: 13 mois à compter de la livraison (gestion des litiges et retours)</li>
          <li><strong>Logs techniques et cookies</strong>&nbsp;: 13 mois maximum</li>
          <li><strong>CV et candidatures spontanées</strong>&nbsp;: 2 ans, sauf opposition</li>
        </ul>

        <h2>7. Vos droits</h2>
        <p>
          Conformément aux articles 15 à 22 du RGPD, vous disposez des droits suivants&nbsp;:
        </p>
        <ul>
          <li><strong>Droit d'accès</strong> à vos données</li>
          <li><strong>Droit de rectification</strong> en cas d'inexactitude</li>
          <li><strong>Droit à l'effacement</strong> («&nbsp;droit à l'oubli&nbsp;»)</li>
          <li><strong>Droit à la limitation</strong> du traitement</li>
          <li><strong>Droit à la portabilité</strong> de vos données</li>
          <li><strong>Droit d'opposition</strong>, notamment au profilage et à la prospection</li>
          <li><strong>Droit de retirer votre consentement</strong> à tout moment, sans effet rétroactif</li>
          <li><strong>Droit de définir des directives</strong> relatives au sort de vos données après votre décès</li>
        </ul>
        <p>
          Pour exercer ces droits, écrivez-nous à <a href={`mailto:${LEGAL.rgpdContact}`}>{LEGAL.rgpdContact}</a>{' '}
          en joignant une preuve d'identité. Nous répondons dans un délai d'un mois maximum.
        </p>

        <h2>8. Sécurité</h2>
        <p>
          Nous mettons en œuvre des mesures techniques et organisationnelles appropriées pour
          protéger vos données contre la perte, l'accès non autorisé, la modification ou la
          divulgation&nbsp;: chiffrement TLS, contrôle d'accès, sauvegardes régulières, sensibilisation
          du personnel, journalisation des accès.
        </p>
        <p>
          En cas de violation de données susceptible d'engendrer un risque pour vos droits et
          libertés, nous notifierons l'Autorité de protection des données dans les 72&nbsp;heures et
          vous informerons le cas échéant.
        </p>

        <h2>9. Réclamations</h2>
        <p>
          Si vous estimez que vos droits ne sont pas respectés, vous pouvez introduire une réclamation
          auprès de l'<strong>Autorité de protection des données</strong>&nbsp;:
        </p>
        <ul>
          <li>Rue de la Presse 35, 1000 Bruxelles</li>
          <li>+32 (0)2 274 48 00</li>
          <li>contact@apd-gba.be</li>
          <li><a href="https://www.autoriteprotectiondonnees.be" target="_blank" rel="noopener noreferrer">www.autoriteprotectiondonnees.be</a></li>
        </ul>

        <h2>10. Modifications</h2>
        <p>
          Nous pouvons mettre à jour cette politique pour refléter des évolutions légales ou
          opérationnelles. La date de dernière mise à jour figure en haut de cette page. Pour
          toute modification substantielle, nous vous en informerons par e-mail ou via une bannière
          sur le site.
        </p>
      </LegalLayout>
    </>
  )
}

function PrivacyPageEn() {
  const { t } = useT()
  const locale = useLocale()
  return (
    <>
      <SeoHead
        title="Privacy Policy — Yaslan"
        description="Privacy Policy of Yaslan Logistics BV. How we collect, use and protect your personal data in accordance with the GDPR."
        path={localizedPath('privacy', locale)}
        jsonLd={[
          orgSchema(locale),
          breadcrumbSchema([
            { name: t('breadcrumb.home'), path: localizedPath('home', locale) },
            { name: t('breadcrumb.privacy'), path: localizedPath('privacy', locale) },
          ]),
        ]}
      />
      <LegalLayout
        breadcrumb="Privacy Policy"
        badge="Legal · GDPR"
        title={
          <>
            Privacy{' '}
            <span className="font-semibold bg-gradient-to-r from-accent-600 via-accent-500 to-accent-700 bg-clip-text text-transparent pb-[0.1em] inline-block">
              Policy
            </span>
            .
          </>
        }
        intro={
          <>
            We handle your personal data with care and in accordance with the General Data Protection
            Regulation (GDPR — EU Regulation 2016/679) and the Belgian law of 30 July 2018.
          </>
        }
        updatedAt="3 August 2026"
      >
        <h2>1. Data Controller</h2>
        <p>
          The data controller for your personal data is&nbsp;:
        </p>
        <ul>
          <li><strong>{LEGAL.company}</strong> ({LEGAL.legalForm}), operating under the brand «&nbsp;{LEGAL.brand}&nbsp;»</li>
          <li>Company number (CBE)&nbsp;: {LEGAL.bce}</li>
          <li>VAT number&nbsp;: {LEGAL.vat}</li>
          <li>Registered office&nbsp;: {LEGAL.registeredOffice.city}, Belgium</li>
          <li>E-mail&nbsp;: <a href={SITE.emailHref}>{SITE.email}</a></li>
          <li>GDPR contact&nbsp;: <a href={`mailto:${LEGAL.rgpdContact}`}>{LEGAL.rgpdContact}</a></li>
        </ul>

        <h2>2. Data We Collect</h2>
        <p>
          We collect only the data strictly necessary to provide our logistics services and manage
          our commercial relationship. Depending on the context&nbsp;:
        </p>
        <h3>2.1. Data provided by our clients (e-commerce brands)</h3>
        <ul>
          <li>Professional identification data&nbsp;: company name, company number, VAT, billing address, name and job title of the contact</li>
          <li>Contact data&nbsp;: professional e-mail, phone number</li>
          <li>Contractual and financial data&nbsp;: contracts, invoices, payment terms</li>
          <li>Login data for our logistics dashboard</li>
        </ul>
        <h3>2.2. Data of parcel recipients (end customers of our clients)</h3>
        <ul>
          <li>Identity&nbsp;: surname, first name</li>
          <li>Contact details&nbsp;: delivery address, e-mail, phone (when transmitted by the carrier)</li>
          <li>Order data&nbsp;: reference, contents, weight, carrier, delivery status</li>
        </ul>
        <p>
          For this data, <strong>{LEGAL.company} acts as a processor</strong> within the meaning of the GDPR&nbsp;:
          the data controller remains the e-commerce brand that mandates us.
        </p>
        <h3>2.3. Data collected via the website yaslan.be</h3>
        <ul>
          <li>Technical data&nbsp;: IP address, browser type, operating system, pages visited, visit duration</li>
          <li>Form data&nbsp;: information voluntarily provided via contact or appointment-booking forms</li>
          <li>Cookies (see our <Link to={localizedPath('cookies', locale)}>Cookie Policy</Link>)</li>
        </ul>

        <h2>3. Purposes and Legal Bases for Processing</h2>
        <p>Your data is processed for the following purposes&nbsp;:</p>
        <ul>
          <li><strong>Performance of a contract</strong> (Art. 6.1.b GDPR)&nbsp;: receipt, storage, preparation and dispatch of orders, returns management, invoicing, customer support</li>
          <li><strong>Legal obligation</strong> (Art. 6.1.c GDPR)&nbsp;: retention of invoices (7 years), VAT declarations, accounting and customs obligations</li>
          <li><strong>Legitimate interest</strong> (Art. 6.1.f GDPR)&nbsp;: improvement of our services, IT security, fraud prevention, B2B commercial prospecting</li>
          <li><strong>Consent</strong> (Art. 6.1.a GDPR)&nbsp;: non-essential cookies, newsletter, marketing communications</li>
        </ul>

        <h2>4. Recipients of Data</h2>
        <p>
          Your data is never sold. It may only be shared with the following categories of recipients,
          to the strictly necessary extent&nbsp;:
        </p>
        <ul>
          <li><strong>Carriers</strong> (bpost, DHL, GLS, UPS, DPD, FedEx, PostNL, etc.) — for parcel delivery</li>
          <li><strong>Integrated e-commerce platforms</strong> (Shopify, WooCommerce, Prestashop, Amazon, etc.) — only at the client's request</li>
          <li><strong>Technical processors</strong>&nbsp;: hosting provider, e-mail provider, accounting tool, payment platform</li>
          <li><strong>Public authorities</strong>&nbsp;: when legally required or by judicial order</li>
        </ul>
        <p>
          All our processors are bound by a Data Processing Agreement (DPA) compliant with
          Article 28 GDPR.
        </p>

        <h2>5. Transfers Outside the European Union</h2>
        <p>
          We favour providers established in the European Union. Where a transfer outside the EU
          is necessary (e.g. an international express carrier), it is governed by the Standard
          Contractual Clauses adopted by the European Commission or by an adequacy decision.
        </p>

        <h2>6. Retention Periods</h2>
        <ul>
          <li><strong>Contractual data and invoices</strong>&nbsp;: 7 years from the end of the contract (Belgian accounting obligation)</li>
          <li><strong>B2B prospecting data</strong>&nbsp;: 3 years from the last contact</li>
          <li><strong>Delivery data</strong>&nbsp;: 13 months from delivery (dispute and returns management)</li>
          <li><strong>Technical logs and cookies</strong>&nbsp;: 13 months maximum</li>
          <li><strong>CVs and unsolicited applications</strong>&nbsp;: 2 years, unless objected to</li>
        </ul>

        <h2>7. Your Rights</h2>
        <p>
          In accordance with Articles 15 to 22 of the GDPR, you have the following rights&nbsp;:
        </p>
        <ul>
          <li><strong>Right of access</strong> to your data</li>
          <li><strong>Right of rectification</strong> in case of inaccuracy</li>
          <li><strong>Right to erasure</strong> («&nbsp;right to be forgotten&nbsp;»)</li>
          <li><strong>Right to restriction</strong> of processing</li>
          <li><strong>Right to data portability</strong></li>
          <li><strong>Right to object</strong>, in particular to profiling and prospecting</li>
          <li><strong>Right to withdraw your consent</strong> at any time, without retroactive effect</li>
          <li><strong>Right to define instructions</strong> regarding the fate of your data after your death</li>
        </ul>
        <p>
          To exercise these rights, write to us at <a href={`mailto:${LEGAL.rgpdContact}`}>{LEGAL.rgpdContact}</a>{' '}
          enclosing proof of identity. We will respond within a maximum of one month.
        </p>

        <h2>8. Security</h2>
        <p>
          We implement appropriate technical and organisational measures to protect your data against
          loss, unauthorised access, alteration or disclosure&nbsp;: TLS encryption, access control,
          regular backups, staff awareness training, access logging.
        </p>
        <p>
          In the event of a data breach likely to pose a risk to your rights and freedoms, we will
          notify the Data Protection Authority within 72&nbsp;hours and inform you where applicable.
        </p>

        <h2>9. Complaints</h2>
        <p>
          If you believe your rights are not being respected, you may lodge a complaint with
          the <strong>Data Protection Authority</strong>&nbsp;:
        </p>
        <ul>
          <li>Rue de la Presse 35, 1000 Brussels</li>
          <li>+32 (0)2 274 48 00</li>
          <li>contact@apd-gba.be</li>
          <li><a href="https://www.autoriteprotectiondonnees.be" target="_blank" rel="noopener noreferrer">www.autoriteprotectiondonnees.be</a></li>
        </ul>

        <h2>10. Amendments</h2>
        <p>
          We may update this policy to reflect legal or operational developments. The date of the
          last update appears at the top of this page. For any substantial change, we will inform
          you by e-mail or via a banner on the site.
        </p>
      </LegalLayout>
    </>
  )
}

function PrivacyPageNl() {
  const { t } = useT()
  const locale = useLocale()
  return (
    <>
      <SeoHead
        title="Privacybeleid — Yaslan"
        description="Privacybeleid van Yaslan Logistics BV. Hoe wij uw persoonsgegevens verzamelen, gebruiken en beschermen in overeenstemming met de AVG."
        path={localizedPath('privacy', locale)}
        jsonLd={[
          orgSchema(locale),
          breadcrumbSchema([
            { name: t('breadcrumb.home'), path: localizedPath('home', locale) },
            { name: t('breadcrumb.privacy'), path: localizedPath('privacy', locale) },
          ]),
        ]}
      />
      <LegalLayout
        breadcrumb="Privacybeleid"
        badge="Juridisch · AVG"
        title={
          <>
            Privacy
            <span className="font-semibold bg-gradient-to-r from-accent-600 via-accent-500 to-accent-700 bg-clip-text text-transparent pb-[0.1em] inline-block">
              beleid
            </span>
            .
          </>
        }
        intro={
          <>
            Wij gaan zorgvuldig om met uw persoonsgegevens en handelen in overeenstemming met de Algemene
            Verordening Gegevensbescherming (AVG — EU-verordening 2016/679) en de Belgische wet van 30 juli 2018.
          </>
        }
        updatedAt="3 augustus 2026"
      >
        <h2>1. Verwerkingsverantwoordelijke</h2>
        <p>
          De verwerkingsverantwoordelijke voor uw persoonsgegevens is&nbsp;:
        </p>
        <ul>
          <li><strong>{LEGAL.company}</strong> ({LEGAL.legalForm}), dat het merk «&nbsp;{LEGAL.brand}&nbsp;» exploiteert</li>
          <li>Ondernemingsnummer (KBO)&nbsp;: {LEGAL.bce}</li>
          <li>Btw-nummer&nbsp;: {LEGAL.vat}</li>
          <li>Maatschappelijke zetel&nbsp;: {LEGAL.registeredOffice.city}, België</li>
          <li>E-mail&nbsp;: <a href={SITE.emailHref}>{SITE.email}</a></li>
          <li>AVG-contact&nbsp;: <a href={`mailto:${LEGAL.rgpdContact}`}>{LEGAL.rgpdContact}</a></li>
        </ul>

        <h2>2. Gegevens die wij verzamelen</h2>
        <p>
          Wij verzamelen uitsluitend de gegevens die strikt noodzakelijk zijn voor het verlenen van
          onze logistieke diensten en het beheer van de handelsrelatie. Afhankelijk van de context&nbsp;:
        </p>
        <h3>2.1. Gegevens verstrekt door onze klanten (e-commercemerken)</h3>
        <ul>
          <li>Professionele identificatiegegevens&nbsp;: naam van het bedrijf, ondernemingsnummer, btw, factuuradres, naam en functie van de contactpersoon</li>
          <li>Contactgegevens&nbsp;: professioneel e-mailadres, telefoonnummer</li>
          <li>Contractuele en financiële gegevens&nbsp;: contracten, facturen, betalingsvoorwaarden</li>
          <li>Inloggegevens voor ons logistiek dashboard</li>
        </ul>
        <h3>2.2. Gegevens van pakketontvangers (eindklanten van onze klanten)</h3>
        <ul>
          <li>Identiteit&nbsp;: achternaam, voornaam</li>
          <li>Contactgegevens&nbsp;: afleveradres, e-mail, telefoon (indien doorgegeven door de vervoerder)</li>
          <li>Bestelgegevens&nbsp;: referentie, inhoud, gewicht, vervoerder, bezorgstatus</li>
        </ul>
        <p>
          Voor deze gegevens <strong>treedt {LEGAL.company} op als verwerker</strong> in de zin van de AVG&nbsp;:
          de verwerkingsverantwoordelijke blijft het e-commercemerk dat ons de opdracht geeft.
        </p>
        <h3>2.3. Gegevens verzameld via de website yaslan.be</h3>
        <ul>
          <li>Technische gegevens&nbsp;: IP-adres, browsertype, besturingssysteem, bezochte pagina's, bezoekduur</li>
          <li>Formuliergegevens&nbsp;: informatie die vrijwillig is ingevuld via de contact- of afspraakformulieren</li>
          <li>Cookies (zie ons <Link to={localizedPath('cookies', locale)}>Cookiebeleid</Link>)</li>
        </ul>

        <h2>3. Doeleinden en rechtsgronden van de verwerking</h2>
        <p>Uw gegevens worden verwerkt voor de volgende doeleinden&nbsp;:</p>
        <ul>
          <li><strong>Uitvoering van de overeenkomst</strong> (art. 6.1.b AVG)&nbsp;: ontvangst, opslag, voorbereiding en verzending van bestellingen, retourverwerking, facturering, klantenondersteuning</li>
          <li><strong>Wettelijke verplichting</strong> (art. 6.1.c AVG)&nbsp;: bewaring van facturen (7 jaar), btw-aangiften, boekhoudkundige en douaneverplichtingen</li>
          <li><strong>Gerechtvaardigd belang</strong> (art. 6.1.f AVG)&nbsp;: verbetering van onze diensten, IT-beveiliging, fraudepreventie, B2B-commerciële prospectie</li>
          <li><strong>Toestemming</strong> (art. 6.1.a AVG)&nbsp;: niet-essentiële cookies, nieuwsbrief, marketingcommunicatie</li>
        </ul>

        <h2>4. Ontvangers van de gegevens</h2>
        <p>
          Uw gegevens worden nooit verkocht. Ze kunnen uitsluitend worden doorgegeven aan de
          volgende categorieën ontvangers, voor zover strikt noodzakelijk&nbsp;:
        </p>
        <ul>
          <li><strong>Vervoerders</strong> (bpost, DHL, GLS, UPS, DPD, FedEx, PostNL, enz.) — voor de bezorging van pakketten</li>
          <li><strong>Geïntegreerde e-commerceplatforms</strong> (Shopify, WooCommerce, Prestashop, Amazon, enz.) — uitsluitend op verzoek van de klant</li>
          <li><strong>Technische verwerkers</strong>&nbsp;: hostingprovider, e-mailprovider, boekhoudtool, betaalplatform</li>
          <li><strong>Overheidsdiensten</strong>&nbsp;: bij een wettelijke verplichting of gerechtelijk bevel</li>
        </ul>
        <p>
          Al onze verwerkers zijn gebonden door een verwerkersovereenkomst (DPA) conform
          artikel 28 AVG.
        </p>

        <h2>5. Doorgiften buiten de Europese Unie</h2>
        <p>
          Wij geven de voorkeur aan dienstverleners die gevestigd zijn in de Europese Unie. Wanneer
          een doorgifte buiten de EU noodzakelijk is (bijv. een internationale expresvervoerder),
          wordt deze geregeld door de Standaard Contractuele Bepalingen die door de Europese Commissie
          zijn vastgesteld, of door een adequaatheidsbesluit.
        </p>

        <h2>6. Bewaartermijnen</h2>
        <ul>
          <li><strong>Contractuele gegevens en facturen</strong>&nbsp;: 7 jaar na afloop van het contract (Belgische boekhoudkundige verplichting)</li>
          <li><strong>B2B-prospectiegegevens</strong>&nbsp;: 3 jaar na het laatste contact</li>
          <li><strong>Bezorggegevens</strong>&nbsp;: 13 maanden na de bezorging (beheer van geschillen en retouren)</li>
          <li><strong>Technische logs en cookies</strong>&nbsp;: maximaal 13 maanden</li>
          <li><strong>CV's en spontane sollicitaties</strong>&nbsp;: 2 jaar, tenzij bezwaar wordt gemaakt</li>
        </ul>

        <h2>7. Uw rechten</h2>
        <p>
          Overeenkomstig de artikelen 15 tot en met 22 van de AVG beschikt u over de volgende rechten&nbsp;:
        </p>
        <ul>
          <li><strong>Recht van inzage</strong> in uw gegevens</li>
          <li><strong>Recht op rectificatie</strong> bij onjuistheden</li>
          <li><strong>Recht op wissing</strong> («&nbsp;recht om vergeten te worden&nbsp;»)</li>
          <li><strong>Recht op beperking</strong> van de verwerking</li>
          <li><strong>Recht op gegevensoverdraagbaarheid</strong></li>
          <li><strong>Recht van bezwaar</strong>, met name tegen profilering en prospectie</li>
          <li><strong>Recht om uw toestemming in te trekken</strong> op elk moment, zonder terugwerkende kracht</li>
          <li><strong>Recht om instructies te geven</strong> over wat er met uw gegevens gebeurt na uw overlijden</li>
        </ul>
        <p>
          Om deze rechten uit te oefenen, schrijft u ons op <a href={`mailto:${LEGAL.rgpdContact}`}>{LEGAL.rgpdContact}</a>{' '}
          met bijvoeging van een identiteitsbewijs. Wij antwoorden binnen maximaal één maand.
        </p>

        <h2>8. Beveiliging</h2>
        <p>
          Wij treffen passende technische en organisatorische maatregelen om uw gegevens te beschermen
          tegen verlies, ongeoorloofde toegang, wijziging of openbaarmaking&nbsp;: TLS-versleuteling,
          toegangscontrole, regelmatige back-ups, bewustmaking van personeel, toegangslogboeken.
        </p>
        <p>
          Bij een datalek dat waarschijnlijk een risico inhoudt voor uw rechten en vrijheden, stellen
          wij de Gegevensbeschermingsautoriteit binnen 72&nbsp;uur in kennis en informeren wij u indien
          van toepassing.
        </p>

        <h2>9. Klachten</h2>
        <p>
          Als u van mening bent dat uw rechten niet worden nageleefd, kunt u een klacht indienen
          bij de <strong>Gegevensbeschermingsautoriteit</strong>&nbsp;:
        </p>
        <ul>
          <li>Rue de la Presse 35, 1000 Brussel</li>
          <li>+32 (0)2 274 48 00</li>
          <li>contact@apd-gba.be</li>
          <li><a href="https://www.autoriteprotectiondonnees.be" target="_blank" rel="noopener noreferrer">www.autoriteprotectiondonnees.be</a></li>
        </ul>

        <h2>10. Wijzigingen</h2>
        <p>
          Wij kunnen dit beleid bijwerken om juridische of operationele ontwikkelingen te weerspiegelen.
          De datum van de laatste update staat bovenaan deze pagina. Bij een ingrijpende wijziging
          zullen wij u informeren per e-mail of via een banner op de site.
        </p>
      </LegalLayout>
    </>
  )
}
